import React, { Dispatch, SetStateAction } from 'react';
import { connect } from 'react-redux';
import { Intl } from '@erecruit/core';
import {
  BaseSearchProps,
  FilterHeaderValuesTemplate,
  FilterHandler,
  OnDateRangeFilter,
  ResetDateFilter,
  createRequestParams,
  createSearchPage,
  createColumnsHook,
} from '@erecruit/modules';
import { Resources, Mutations } from '@erecruit/types';

import {
  selectors,
  searchMatches,
  getFilterData,
  exportMatches,
  MatchSearchFilterData,
} from './state/reducer';

import {
  generateColumns,
  parseTableFilters,
  MatchColumnType,
  defaultVisibleColumns,
  getDefinedMatchReportColumns,
} from './columns';
import { MatchParams } from '../../App/types';

const { FormattedMessage } = Intl;

export interface MatchSearchProps
  extends BaseSearchProps<
      Resources.MatchSearch,
      MatchSearchFilterData,
      Resources.MatchSearchFilter
    >,
    Intl.InjectedIntlProps {}

const matchesTemplateValues: FilterHeaderValuesTemplate<MatchParams>[] = [
  {
    value: 'All',
    label: <FormattedMessage id={messageIds.vms.matches.all} />,
  },
  {
    value: 'Submittal',
    icon: 'users',
    label: <FormattedMessage id={messageIds.vms.matches.submittals} />,
  },
  {
    value: 'PendingStart',
    icon: 'check',
    label: <FormattedMessage id={messageIds.vms.matches.pendingStart} />,
  },
  {
    value: 'PendingMyApproval',
    icon: 'approvals',
    label: <FormattedMessage id={messageIds.vms.matches.pendingMyApproval} />,
  },
  {
    value: 'FullPlacement',
    icon: 'match',
    label: <FormattedMessage id={messageIds.vms.matches.currentlyWorking} />,
  },
  {
    value: 'EndingSoon',
    icon: 'time',
    label: <FormattedMessage id={messageIds.vms.matches.endingSoon} />,
  },
];

function getFilterImplementation(
  filterParams: Resources.MatchSearchParams,
  setFilterParams: Dispatch<SetStateAction<Resources.MatchSearchParams>>,
): Record<string, FilterHandler> {
  const filterByDate: OnDateRangeFilter = (dateFieldKey, dateRange) => {
    setFilterParams({
      ...filterParams,
      filter: { ...filterParams.filter, [dateFieldKey]: dateRange },
    });
  };

  const resetDateFilter: ResetDateFilter = dateFieldKey => {
    if (
      filterParams.filter &&
      !filterParams.filter[dateFieldKey as keyof Resources.MatchSearchFilter]
    )
      return;
    const {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      [dateFieldKey as keyof Resources.MatchSearchFilter]: omit,
      ...other
    } = filterParams.filter as Resources.MatchSearchFilter;

    setFilterParams({
      ...filterParams,
      filter: { ...other },
    });
  };

  return {
    filterByDate,
    resetDateFilter,
  };
}

function generateColumnsFactory(
  filters: Record<string, FilterHandler>,
  filterData: Resources.MatchSearchFilter,
  locationSearch: string,
  queryType: MatchParams,
  propsFilterData?: MatchSearchFilterData,
) {
  const filterByDate = filters['filterByDate'] as OnDateRangeFilter;
  const resetDateFilter = filters['resetDateFilter'] as ResetDateFilter;

  return generateColumns(
    filterByDate,
    resetDateFilter,
    filterData,
    locationSearch,
    propsFilterData,
    queryType,
  );
}

export const useColumns = createColumnsHook(
  getFilterImplementation,
  generateColumnsFactory,
);

const convertToMatchSearchType = (
  value: MatchParams,
): Resources.MatchSearchType | undefined => {
  switch (value) {
    case 'All':
      return Resources.MatchSearchType.All;
    case 'FullPlacement':
      return Resources.MatchSearchType.Working;
    case 'Submittal':
      return Resources.MatchSearchType.Submittals;
    case 'EndingSoon':
      return Resources.MatchSearchType.EndingSoon;
    case 'PendingStart':
      return Resources.MatchSearchType.PendingStart;
    case 'PendingMyApproval':
      return Resources.MatchSearchType.PendingMyApproval;
    default:
      return undefined;
  }
};

function getRequestParams(type: MatchParams) {
  const data: Resources.MatchSearchFilter = {
    searchTerm: '',
    matchSearchType: convertToMatchSearchType(type),
  };

  return createRequestParams(
    data,
    type === 'Submittal'
      ? nameof<Resources.MatchSearch>(o => o.submittedOn)
      : nameof<Resources.MatchSearch>(o => o.createdOn),
  );
}

const getReportColumns = (
  intl: Intl.InjectedIntl,
  visibleColumns: string[],
): Mutations.ColumnDefinition[] => {
  const allDefinedColumns = getDefinedMatchReportColumns(intl);
  const visibleColumnsIds = visibleColumns.map(
    visibleColumn => visibleColumn.split('.')[0],
  );
  return allDefinedColumns.filter(
    reportColumn =>
      visibleColumnsIds.findIndex(
        visibleColumnId => reportColumn.id === visibleColumnId,
      ) !== -1,
  );
};

export const MatchSearch = createSearchPage<
  Resources.MatchSearchFilter,
  MatchSearchProps,
  MatchColumnType,
  MatchParams,
  Resources.MatchSearch,
  MatchSearchFilterData
>({
  title: <FormattedMessage id={messageIds.vms.matches.matches} />,
  searchTermPlaceholderText: 'Search Matches',
  baseQueryRoute: 'matchType',
  defaultSearchQuery: 'All',
  alwaysVisibleColumns: ['id'],
  rowKey: nameof<Resources.MatchSearch>(o => o.id),
  filterHeaderValues: matchesTemplateValues,
  getReportColumns,
  useColumns,
  getRequestParams,
  parseFilterParams: (filters, columns, filterParams) => {
    const parsedFilters = parseTableFilters(filters, columns);

    return {
      ...parsedFilters,
      matchStatusTypes: filterParams?.matchStatusTypes,
      searchTerm: filterParams?.searchTerm,
      estimatedStartDate: filterParams?.estimatedStartDate,
      estimatedEndDate: filterParams?.estimatedEndDate,
      createdOn: filterParams?.createdOn,
      matchSearchType: filterParams?.matchSearchType,
    };
  },
  getDefaultVisibleColumns: type =>
    defaultVisibleColumns[type] || defaultVisibleColumns.All,
});

export default connect(selectors.selectMatchSearchState, {
  searchEntities: searchMatches,
  exportAction: exportMatches,
  getFilterData,
})(MatchSearch);
